import { __ } from "@wordpress/i18n";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import ChartLegend from "../../../../components/ChartLegend/ChartLegend";
import { useResult } from "../../../../shared/formContext/hooks/useResult";
import { useForm } from "../../../../shared/formContext/hooks/useForm";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

import { useState, useEffect, useRef } from '@wordpress/element';

/**
 * Chart colors configuration
 */
const CHART_COLORS = {
  CAPITAL_TOTAL: "#09C",
  MONTANTE_INVESTIDO: "#CCEBF5", 
  RETORNO: "#000031",
} as const;

const CHART_LEGENDS = {
  CAPITAL_TOTAL: __("Capital total", "df-simulators"),
  MONTANTE_INVESTIDO: __("Montante investido", "df-simulators"),
  RETORNO: __("Retorno", "df-simulators"),
} as const;

/**
 * TabInvestmentEvolution Component
 *
 * Standalone implementation with direct Chart.js integration
 * Ensures tooltip and legend functionality work perfectly together
 *
 * @returns {JSX.Element | null} The chart component or null if no data
 */
const TabInvestmentEvolution = (): JSX.Element | null => {
  const { resultData } = useResult();
  const { formData } = useForm();
  const { response } = resultData;

  // Extract retirement data
  const retirementData = response?.data?.result?.data;
  
  // Get the contribution frequency from submitted form data
  const contributionFrequency = formData.submitted?.contribution_frequency || 'monthly';
  
  // Use appropriate evolution data based on frequency
  // If monthly, use perPeriod (monthly data), otherwise use perYear (yearly data)
  const evolution = contributionFrequency === 'monthly' 
    ? (retirementData?.evolution?.perPeriod || [])
    : (retirementData?.evolution?.perYear || []);

  const [legends, setLegends] = useState();
  const [chartData, setChartData] = useState(null);
  const [tooltipDataIndex, setTooltipDataIndex] = useState();
  const chartRef = useRef(null);

  /**
   * Updates legend values dynamically for all lines
   */
  const updateLegend = (tooltipItem: any) => {
    const dataIndex = tooltipItem.dataIndex;
    const dataPoint = evolution[dataIndex];

    if (!dataPoint) return;

    setLegends((previousLegends: any) => ({
      ...previousLegends,
      [CHART_LEGENDS.CAPITAL_TOTAL]: {
        ...previousLegends[CHART_LEGENDS.CAPITAL_TOTAL],
        value: dataPoint.end_balance || 0,
      },
      [CHART_LEGENDS.MONTANTE_INVESTIDO]: {
        ...previousLegends[CHART_LEGENDS.MONTANTE_INVESTIDO],
        value: dataPoint.acc_contributed || 0,
      },
      [CHART_LEGENDS.RETORNO]: {
        ...previousLegends[CHART_LEGENDS.RETORNO],
        value: dataPoint.acc_interest || 0,
      },
    }));
  };

  useEffect(() => {
    if (!Array.isArray(evolution) || evolution.length === 0) return;

    // Initialize legend
    setLegends({
      [CHART_LEGENDS.CAPITAL_TOTAL]: {
        value: retirementData?.future_value || 0,
        color: CHART_COLORS.CAPITAL_TOTAL,
      },
      [CHART_LEGENDS.MONTANTE_INVESTIDO]: {
        value: retirementData?.total_contribution || 0,
        color: CHART_COLORS.MONTANTE_INVESTIDO,
      },
      [CHART_LEGENDS.RETORNO]: {
        value: retirementData?.total_interest || 0,
        color: CHART_COLORS.RETORNO,
      },
    });

    // Generate chart data with appropriate labels based on frequency
    const periodLabel = contributionFrequency === 'monthly' 
      ? __("Mês", "df-simulators") 
      : __("Ano", "df-simulators");
      
    const labels = evolution.map((item, index) => {
      // Show labels only at first and last points
      if (index === 0 || index === evolution.length - 1) {
        if (contributionFrequency === 'monthly') {
          // For monthly, use the period number from API data
          return periodLabel + " " + (item.period || index + 1);
        } else {
          // For yearly, use the year number from API data
          return periodLabel + " " + (item.year || index + 1);
        }
      }
      return "";
    });

    const capitalTotal = evolution.map((item) => item.end_balance || 0);
    const montanteInvestido = evolution.map((item) => item.acc_contributed || 0);
    const retorno = evolution.map((item) => item.acc_interest || 0);

    setChartData({
      labels,
      datasets: [
        {
          label: CHART_LEGENDS.CAPITAL_TOTAL,
          data: capitalTotal,
          borderColor: CHART_COLORS.CAPITAL_TOTAL,
          pointBackgroundColor: CHART_COLORS.CAPITAL_TOTAL,
          pointBorderWidth: 6,
          fill: false,
          tension: 0.1,
        },
        {
          label: CHART_LEGENDS.MONTANTE_INVESTIDO,
          data: montanteInvestido,
          borderColor: CHART_COLORS.MONTANTE_INVESTIDO,
          pointBackgroundColor: CHART_COLORS.MONTANTE_INVESTIDO,
          pointBorderWidth: 6,
          fill: false,
          tension: 0.1,
        },
        {
          label: CHART_LEGENDS.RETORNO,
          data: retorno,
          borderColor: CHART_COLORS.RETORNO,
          pointBackgroundColor: CHART_COLORS.RETORNO,
          pointBorderWidth: 6,
          fill: false,
          tension: 0.1,
        },
      ],
    });
  }, [response]);

  /**
   * Chart.js options with perfect tooltip and legend integration
   */
  const chartOptions = {
    layout: {
      padding: { top: 0, bottom: 0 },
    },
    borderWidth: 4,
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    plugins: {
      title: { display: false },
      legend: { display: false },
      tooltip: {
        displayColors: false,
        bodyAlign: "center" as const,
        titleAlign: "center" as const,
        titleMarginBottom: 0,
        filter: (tooltipItem: any) => {
          // Only show tooltip for montante investido line
          return (
            tooltipItem.dataset.label === CHART_LEGENDS.MONTANTE_INVESTIDO
          );
        },
        callbacks: {
          title: (tooltipItems: any[]) => {
            const dataItem = evolution[tooltipItems[0].dataIndex];
            const periodLabel = contributionFrequency === 'monthly' ? 'Mês' : 'Ano';
            
            if (contributionFrequency === 'monthly') {
              // For monthly, use the period number from API data
              return `${periodLabel} ${dataItem.period || tooltipItems[0].dataIndex + 1}`;
            } else {
              // For yearly, use the year number from API data  
              return `${periodLabel} ${dataItem.year || tooltipItems[0].dataIndex + 1}`;
            }
          },
          label: (tooltipItem: any) => {
            const today = new Date();
            
            if (contributionFrequency === 'monthly') {
              // For monthly frequency, show month name and year (e.g., "Janeiro, 2025")
              const dataItem = evolution[tooltipItem.dataIndex];
              const monthsFromNow = dataItem.period || tooltipItem.dataIndex + 1;
              const futureDate = new Date(today);
              futureDate.setMonth(futureDate.getMonth() + monthsFromNow);
              
              const monthNames = [
                __("Janeiro", "df-simulators"), __("Fevereiro", "df-simulators"), __("Março", "df-simulators"),
                __("Abril", "df-simulators"), __("Maio", "df-simulators"), __("Junho", "df-simulators"),
                __("Julho", "df-simulators"), __("Agosto", "df-simulators"), __("Setembro", "df-simulators"),
                __("Outubro", "df-simulators"), __("Novembro", "df-simulators"), __("Dezembro", "df-simulators")
              ];
              
              const monthName = monthNames[futureDate.getMonth()];
              const year = futureDate.getFullYear();
              return `${monthName}, ${year}`;
            } else {
              // For yearly frequency, show just the year
              return today.getFullYear() + tooltipItem.dataIndex + 1;
            }
          },
          footer: () => "",
          beforeLabel: (tooltipItem: any) => {
            if (Number(tooltipItem.dataIndex) !== Number(tooltipDataIndex)) {
              setTooltipDataIndex(tooltipItem.dataIndex);
              updateLegend(tooltipItem);
            }
          },
        },
      },
    },
    scales: {
      y: {
        display: false,
      },
      x: {
        display: true,
        grid: { display: false },
        ticks: {
          display: true,
          font: {
            family: "'Proxima Nova', sans-serif",
            size: 12,
            weight: "bold" as const,
          },
          color: "#5F5F7C",
          rotation: 0,
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false,
          align: "center" as const,
        },
      },
    },
  };

  if (!chartData) return null;

  return (
    <div className="tab-investment-evolution">
      <div className="chart-line">
        <Line
          ref={chartRef}
          data={chartData}
          options={chartOptions}
        />
      </div>
      {legends && (
        <div className="mt-4 pt-4">
          <ChartLegend data={legends} horizontal />
        </div>
      )}
    </div>
  );
};

export default TabInvestmentEvolution;
